// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/planning/USER_MANAGEMENT_UI_PLAN_COMPLETE.md
/**
 * User Management Main Component
 * 
 * Main dashboard for user management system providing:
 * - User statistics overview
 * - User list with filtering and search
 * - User creation and editing capabilities
 * - Password management
 * - Permission management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Container,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Lock as LockIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';

// Import sub-components
import UserStatistics from './UserStatistics';
import UserList from './UserList';
import UserFilters from './UserFilters';
import UserForm from './UserForm';
import PasswordManagement from './PasswordManagement';
import RoleManagement from '../RoleManagement/RoleManagement';
import TeamManagement from '../TeamManagement/TeamManagement';
import PageSectionHeader from '../common/PageSectionHeader';

// Import service
import userManagementService from '../../services/userManagementService';

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    role_id: '',
    status: 'active',
    company_id: '',
    department_id: ''
  });
  
  // Dialog states
  const [openUserForm, setOpenUserForm] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 25,
    total: 0
  });

  useEffect(() => {
    loadUsers();
  }, [filters, pagination.page]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare is_active filter based on status
      let is_active = undefined;
      let include_inactive = false;
      
      if (filters.status === 'active') {
        is_active = true;
      } else if (filters.status === 'inactive') {
        is_active = false;
      } else if (filters.status === 'all') {
        include_inactive = true;
      }
      
      const response = await userManagementService.user.list({
        skip: pagination.page * pagination.limit,
        limit: pagination.limit,
        is_active: is_active,
        include_inactive: include_inactive,
        role_id: filters.role_id || undefined,
        company_id: filters.company_id || undefined,
        department_id: filters.department_id || undefined,
        search: filters.search || undefined
      });
      
      setUsers(response.users || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
      
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setOpenUserForm(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setOpenUserForm(true);
  };

  const handlePasswordManagement = (user) => {
    setSelectedUser(user);
    setOpenPasswordDialog(true);
  };

  const handleUserSaved = () => {
    setOpenUserForm(false);
    setEditingUser(null);
    setSuccess('User saved successfully');
    loadUsers();
  };

  const handleUserDeleted = () => {
    setSuccess('User deleted successfully');
    loadUsers();
  };

  const handlePasswordChanged = () => {
    setOpenPasswordDialog(false);
    setSelectedUser(null);
    setSuccess('Password updated successfully');
  };

  const handlePermissionsUpdated = () => {
    setOpenPermissionDialog(false);
    setSelectedUser(null);
    setSuccess('Permissions updated successfully');
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      <PageSectionHeader
        icon={<PeopleIcon />}
        title="User Management"
        description="Manage users, roles, permissions, and access control"
      />

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500
            }
          }}
        >
          <Tab icon={<PeopleIcon />} label="Users" iconPosition="start" />
          <Tab icon={<LockIcon />} label="Roles & Permissions" iconPosition="start" />
          <Tab icon={<GroupsIcon />} label="Team Management" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tab 0: Users */}
      {activeTab === 0 && (
        <>
          {/* Statistics Cards */}
          <UserStatistics users={users} />

          {/* Filters and Actions */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <UserFilters 
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                />
              </Box>
              <Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateUser}
                  size="large"
                  sx={{ 
                    bgcolor: 'primary.main',
                    height: '56px',
                    px: 3,
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Add User
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* User List */}
          <Card>
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <UserList
                  users={users}
                  pagination={pagination}
                  onEditUser={handleEditUser}
                  onDeleteUser={handleUserDeleted}
                  onPasswordManagement={handlePasswordManagement}
                  onPageChange={handlePageChange}
                  onBulkEditComplete={loadUsers}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Tab 1: Roles & Permissions */}
      {activeTab === 1 && (
        <RoleManagement />
      )}

      {/* Tab 2: Team Management */}
      {activeTab === 2 && (
        <TeamManagement />
      )}

      {/* Dialogs */}
      <UserForm
        open={openUserForm}
        onClose={() => setOpenUserForm(false)}
        user={editingUser}
        onSave={handleUserSaved}
      />

      <PasswordManagement
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
        user={selectedUser}
        onPasswordChanged={handlePasswordChanged}
      />
    </Container>
  );
};

export default UserManagement;
